/**
 * Elder profile (name + time zone). Voice / rate / pitch live on the
 * Voice page now. Used inside the combined Settings page.
 *
 * Two modes:
 *  - Initial setup (elderName empty): an explicit form with a Continue
 *    button that saves both fields and forwards into the events flow —
 *    a one-off, completed as a unit.
 *  - Steady state: per-field save-on-change (same pattern as the Voice
 *    page). The name commits on blur / after typing pauses and never
 *    saves empty; the time zone commits on selection. Each write patches
 *    only its own field, so co-caregivers can't clobber each other, and
 *    external changes flow straight into the fields.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAccount } from '@/hooks/useAccount';
import { useAccountAutosave } from '@/hooks/useAccountAutosave';
import { useCalendars } from '@/hooks/useCalendars';
import { useDebouncedCommit } from '@/hooks/useDebouncedCommit';
import { useMyRole } from '@/hooks/useMyRole';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { HealthConsentCheckbox } from '@/components/HealthConsentCheckbox';
import { saveElderProfileWithConsent } from '@/lib/firestore';
import { useAuth } from '@/hooks/useAuth';
import { detectTimeZone, timeZoneOptions } from '@/lib/timeZone';

interface Props {
  aid: string;
}

export function ProfileSection({ aid }: Props) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { account, loading } = useAccount(aid);
  const { calendars } = useCalendars(aid);
  // Editing the elder's details writes accounts/{aid}, which firestore.rules
  // gates to admins. Show caregivers a read-only view rather than a form that
  // would fail on save. `role` is null until the members snapshot lands — we
  // hold the spinner in that window rather than defaulting to the caregiver
  // view, so a fresh admin mid-onboarding doesn't flash "only an admin can
  // change these" at their own just-created account.
  const role = useMyRole(aid);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isInitialSetup = !!account && !account.elderName.trim();

  // Initial setup uses PLAIN local state — it's an explicit one-shot form,
  // and it must not touch the autosave fields below: a no-op "commit" still
  // clears the field's pending buffer, so a debounce tick (or the blur that
  // clicking Continue fires) would visibly wipe the half-typed name back to
  // the empty doc value and block the submit. (Shipped bug — v0.69.0.)
  const [setupName, setSetupName] = useState('');
  const [setupTz, setSetupTz] = useState(detectTimeZone);
  // First naming requires the explicit health-data consent (rules enforce
  // it), the same as Welcome / SetupDevice / AddPersonDialog — this Settings
  // form is the fourth first-naming surface (legacy /profile redirects land
  // here), and shipping it without the checkbox left users permission-denied
  // with no way through.
  const [setupConsent, setSetupConsent] = useState(false);
  // Seed the setup tz from the account doc once it lands — signup seeds the
  // ELDER's timezone server-side, and the browser's tz belongs to the
  // CAREGIVER, who may be setting this up from anywhere. The browser value
  // stays as the pre-account fallback; a user choice always wins.
  const setupTzTouched = useRef(false);
  const setupTzSeeded = useRef(false);
  useEffect(() => {
    if (!setupTzSeeded.current && account?.timeZone && !setupTzTouched.current) {
      setupTzSeeded.current = true;
      setSetupTz(account.timeZone);
    }
  }, [account?.timeZone]);

  // Steady-state autosave. The setup form never renders these fields, so
  // no commit can originate while elderName is still empty.
  const { save, status: saveStatus } = useAccountAutosave(aid, role === 'admin');
  const nameField = useDebouncedCommit<string>(
    account?.elderName ?? '',
    (v) => {
      const trimmed = v.trim();
      // Empty never saves — the buffer clears and the field visibly
      // reverts to the saved name instead of wiping it.
      if (!trimmed || trimmed === account?.elderName) {
        return Promise.resolve();
      }
      return save({ elderName: trimmed });
    },
    800,
  );
  const tzField = useDebouncedCommit<string>(
    account?.timeZone || detectTimeZone(),
    (v) => {
      if (v === account?.timeZone) return Promise.resolve();
      return save({ timeZone: v });
    },
    0,
  );

  // Initial setup: explicit one-shot save of both fields + forward into
  // the events flow, so the journey feels continuous.
  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (auth.state !== 'signedIn') return;
    // HTML `required` accepts whitespace — a " " name would complete setup
    // with an empty elderName fanned out everywhere.
    if (!setupName.trim() || !setupConsent) return;
    setBusy(true);
    setError(null);
    try {
      // fan_out_account_name fans elderName out to memberships; we
      // only write accounts/{aid} from the client.
      await saveElderProfileWithConsent(
        aid,
        { elderName: setupName.trim(), timeZone: setupTz },
        auth.user.uid,
      );
      const primary = calendars.find((c) => c.isPrimary) ?? calendars[0];
      if (primary) {
        navigate(`/accounts/${aid}/calendars/${primary.id}/events`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  if (loading || role === null) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }
  if (!account) {
    return <div className="text-sm text-ink-muted">Account not found.</div>;
  }

  if (role !== 'admin') {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-soft">
            First name of the person you care for
          </span>
          <p className="text-sm text-ink">{account.elderName || '—'}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-soft">Time zone</span>
          <p className="text-sm text-ink">{account.timeZone || detectTimeZone()}</p>
        </div>
        <div className="rounded-md bg-paper-alt px-3 py-2 text-sm text-ink-soft">
          Only an admin can change these details.
        </div>
      </div>
    );
  }

  if (isInitialSetup) {
    // Plain controlled inputs — see the setupName/setupTz comment above.
    return (
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <p className="text-sm text-ink-soft">
          We'll greet them by name and use the right time zone for spoken summaries.
        </p>
        <Field label="First name of the person you care for" htmlFor="elderName">
          <Input
            id="elderName"
            value={setupName}
            onChange={(e) => setSetupName(e.target.value)}
            required
            maxLength={100}
            placeholder="First name"
          />
        </Field>
        <Field label="Time zone" htmlFor="timeZone">
          <select
            id="timeZone"
            className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
            value={setupTz}
            onChange={(e) => {
              setupTzTouched.current = true;
              setSetupTz(e.target.value);
            }}
          >
            {timeZoneOptions(setupTz).map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </Field>
        <HealthConsentCheckbox
          checked={setupConsent}
          onCheckedChange={setSetupConsent}
          idPrefix="settings-setup"
        />
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        <div>
          <Button type="submit" disabled={busy || !setupName.trim() || !setupConsent}>
            {busy ? 'Saving…' : 'Continue'}
          </Button>
        </div>
      </form>
    );
  }

  // Steady state: fields save themselves; inline status instead of a button.
  return (
    <div className="flex flex-col gap-4">
      <Field label="First name of the person you care for" htmlFor="elderName">
        <Input
          id="elderName"
          value={nameField.value}
          onChange={(e) => nameField.set(e.target.value)}
          onBlur={nameField.flush}
          required
          maxLength={100}
          placeholder="First name"
        />
      </Field>
      <Field label="Time zone" htmlFor="timeZone">
        <select
          id="timeZone"
          className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
          value={tzField.value}
          onChange={(e) => tzField.set(e.target.value)}
        >
          {timeZoneOptions(tzField.value).map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </Field>
      <div
        className="min-h-5 text-sm"
        role={saveStatus.kind === 'error' ? 'alert' : 'status'}
        aria-live={saveStatus.kind === 'error' ? 'assertive' : 'polite'}
      >
        {saveStatus.kind === 'error' ? (
          <span className="text-red-700">Couldn't save — {saveStatus.message || 'try again'}</span>
        ) : saveStatus.kind === 'pending' ? (
          <span className="text-ink-soft">Saving…</span>
        ) : saveStatus.kind === 'saved' ? (
          <span className="text-green-700">✓ Saved</span>
        ) : null}
      </div>
    </div>
  );
}
